import React, { useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _compact from 'lodash/compact';
import { makeStyles } from '@mui/styles';
import { Button, Divider, Grid, Tooltip } from '@mui/material';
import { shallowEqual, useSelector } from 'react-redux';

import MText from 'components/MaterialUI/MText';
import { shortName } from 'utils/names';
import { IconHotel } from 'assets/icons';
import { getRubles, turnOffEvent } from 'utils/helpers';
import { getMomentDate } from 'utils/moment';
import MGrid from 'components/MaterialUI/MGrid';
import MPaper from 'components/MaterialUI/MPaper';
import HotelInfo from 'pages/Orders/HotelOrders/HotelInfo/HotelInfo';
import FormInputDisplay from 'components/FormElements/FormInputDisplay';
import { getHotelStatusColor, getHotelStatusName } from 'pages/Orders/helpers';
import CancelHotelPopover from 'pages/Orders/HotelOrders/CancelPopover';
import CancelPenalties from 'pages/Hotels/components/CancelPenalties';
import DocumentOptions from 'pages/Orders/HotelOrders/HotelInfo/DocumentOptions';
import MTooltip from 'components/MaterialUI/MTooltip';
import { getUserDataSelector } from 'reactStore/selectors/userSelector';

const useStyles = makeStyles((theme) => ({
  icon: {
    width: 32,
    height: 32,
    paddingLeft: 3,
    color: '#FFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey5.main,
    borderRadius: '50%',
  },
  open: {
    border: `1px solid ${theme.palette.info.main}`,
  },
  hiddenText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 145,
  },
}));

const HotelOrdersListCard = (props) => {
  const {
    isLoading,
    order,
    order: { ['order_items']: orderItems = [] },
  } = props;
  const user = useSelector(getUserDataSelector, shallowEqual);
  const orderId = order?.['id'];
  const stateStatus = order?.['state'];

  const [anchorEl, setAnchorEl] = useState(null);

  const {
    totalOrderAmount,
    originalPrice,
    hotel,
    country,
    region,
    voucher,
    currentOrder,
    orderStatus,
    supplierReservation,
    cancellationPenalties,
    freeCancellationBefore,
    orderItemId,
    checkin,
    checkout,
  } = orderItems.reduce(
    (amount, orderItem) => {
      const hotel = orderItem?.['reservation']?.['hotel'];
      const reservation = orderItem?.['reservation'];
      const voucher = orderItem?.['voucher'];
      const currentOrder = orderItem;

      const totalOrderAmount = reservation?.actual_sell_price?.order_item_total?.gross?.cents || 0;
      const originalPrice = reservation?.initial_sell_price?.provider_total?.gross?.cents || 0;

      return {
        hotel: hotel?.name,
        country: hotel.region?.country_name,
        region: hotel.region?.name,
        voucher,
        currentOrder,
        orderStatus: reservation?.state,
        supplierReservation: reservation?.supplier_reservation_id,
        cancellationPenalties: reservation?.rate?.cancellation_penalties,
        freeCancellationBefore: reservation?.rate?.free_cancellation_before,
        orderItemId: currentOrder?.id,
        checkin: reservation.checkin,
        checkout: reservation.checkout,
        totalOrderAmount,
        originalPrice,
      };
    },
    {
      totalOrderAmount: 0,
      originalPrice: 0,
    },
  );
  const reservations = supplierReservation?.map((item) => _compact(item).join(', '));

  const getNames = () => {
    return orderItems
      ?.map((order) => {
        return order?.travellers?.map((traveller) =>
          shortName({
            first_name: traveller.first_name,
            last_name: traveller.last_name,
            middle_name: traveller.middle_name,
          }),
        );
      })
      .join(', ');
  };

  const handleCancel = (e) => {
    turnOffEvent(e);
    setAnchorEl(e.currentTarget);
  };

  const renderTooltip = () => {
    return (
      <Tooltip
        arrow
        placement="top"
        title={
          <CancelPenalties
            cancellationPenalties={cancellationPenalties}
            freeCancellationBefore={freeCancellationBefore}
          />
        }
      >
        <MText variant="subtitle1" sx={{ color: 'info.main' }} children={t('Hotels.CancelPolicy')} />
      </Tooltip>
    );
  };

  const classes = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isAllPaid = orderItems.every(({ payment_intent }) => payment_intent?.state === 'paid');
  const isPersonEqualTraveller =
    (currentOrder?.travellers?.some(({ person_id }) => person_id === order?.owner?.id) &&
      user?.role === 'self_booker') ||
    user?.role === 'manager';

  return (
    <Grid item xs={12}>
      <MPaper withGrid className={clsx({ [classes.open]: open })} gridProps={{ rowSpacing: 4 }}>
        <MGrid size={12} columnSpacing={3} alignItems="center" justifyContent="space-between">
          <MGrid columnSpacing={6} alignItems="center" justifyContent="flex-start">
            <Grid item children={<div className={classes.icon} children={<IconHotel />} />} />

            <Grid item>
              <MText variant="subtitle1" color="textSecondary" children={`${t('Orders.Order')}:`} />
              <MText {...{ isLoading }} width={130} variant="h3" weight="bold" children={order?.['code']} />
              <MText
                {...{ isLoading }}
                width={70}
                variant="subtitle2"
                sx={{ color: 'info.main' }}
                className={getHotelStatusColor(stateStatus, classes)}
                children={getHotelStatusName(stateStatus)}
              />
            </Grid>

            <FormInputDisplay
              {...{ isLoading }}
              label={t('Orders.CreatedAt')}
              value={getMomentDate(order['created_at'])}
            />
            <FormInputDisplay
              {...{ isLoading }}
              label={t('Orders.ChangedAt')}
              value={getMomentDate(order['updated_at'])}
            />
            <MTooltip arrow placement="top" title={order['owner']?.['email'] || ''}>
              <Grid item>
                <FormInputDisplay
                  {...{ isLoading }}
                  label={t('Users.User')}
                  className={classes.hiddenText}
                  value={order['owner']?.['email']}
                />
              </Grid>
            </MTooltip>
          </MGrid>

          <MGrid alignItems="center" columnSpacing={3}>
            {voucher && <DocumentOptions {...{ orderItem: currentOrder, orderCode: order?.['code'] }} />}
            {orderStatus === 'confirmed' && anchorEl !== 'voluntary' && <Grid item>{renderTooltip()}</Grid>}
            {orderStatus === 'confirmed' && anchorEl !== 'voluntary' && isPersonEqualTraveller && (
              <Grid item>
                <Button size="small" variant="outlined" onClick={handleCancel} children={t('Orders.RequestRefund')} />
              </Grid>
            )}
            <Grid item>
              <Button
                size="small"
                variant="text"
                color="inherit"
                disabled={isLoading}
                onClick={() => setOpen((old) => !old)}
              >
                {open ? t('Actions.Hide') : t('Actions.Show')}
              </Button>
            </Grid>
          </MGrid>
        </MGrid>

        <Grid item xs={12} children={<Divider />} />

        <MGrid size={12} rowSpacing={3} columnSpacing={6} justifyContent="flex-start">
          <Grid item children={<div style={{ width: 32 }} />} />

          <MGrid rowSpacing={3} direction="column">
            <Grid item>
              <MText variant="subtitle1" color="textSecondary" children={`${t('Orders.ToPay')}:`} />
              <MText {...{ isLoading }} variant="h3" weight="bold" children={getRubles(totalOrderAmount)} />
            </Grid>
            <FormInputDisplay {...{ isLoading }} label={t('Orders.Payed')} value={isAllPaid ? 'Да' : 'Нет'} />
          </MGrid>

          <MGrid size={10} spacing={3} sx={{ ml: 10 }}>
            <FormInputDisplay {...{ isLoading }} size={2} label={t('Orders.Hotel')} value={hotel} />
            <FormInputDisplay {...{ isLoading }} size={2} label={t('Orders.Country')} value={country} isBold />
            <FormInputDisplay {...{ isLoading }} size={2} label={t('Orders.Region')} value={region} isBold />
            <FormInputDisplay {...{ isLoading }} size={2} label={t('Orders.Guests')} value={getNames()} />
            <FormInputDisplay {...{ isLoading }} size={2} label={t('Orders.Bookings')} value={reservations} />
          </MGrid>

          <MGrid size={10} spacing={3} sx={{ ml: 10 }}>
            <FormInputDisplay size={2} label={t('Hotels.Checkin')} value={getMomentDate(checkin)} />
            <FormInputDisplay size={2} label={t('Hotels.Checkout')} value={getMomentDate(checkout)} />
          </MGrid>

          {open && (
            <MGrid size={12} spacing={6}>
              <Grid item children={<div style={{ width: 32 }} />} />
              <MGrid size={10} spacing={3}>
                <FormInputDisplay size={2} label={t('Orders.OriginalPrice')} value={getRubles(originalPrice)} />
                <FormInputDisplay
                  size={2}
                  label={t('Orders.LeftToPay')}
                  value={isAllPaid ? '0 ₽' : getRubles(Math.abs(totalOrderAmount))}
                />
                <FormInputDisplay size={2} label={t('Orders.BackSum')} value="-" />
                <FormInputDisplay size={2} label={t('Orders.Contract')} value={order['contract']?.['number']} />
                <FormInputDisplay size={2} label={t('Orders.InformationAccount')} value="-" />
              </MGrid>

              <HotelInfo {...{ orderItems, orderId }} />
            </MGrid>
          )}
        </MGrid>
      </MPaper>
      <CancelHotelPopover {...{ anchorEl, freeCancellationBefore, orderItemId, setOpen }} onClose={setAnchorEl} />
    </Grid>
  );
};

HotelOrdersListCard.propTypes = {
  order: PropTypes.object,
  isLoading: PropTypes.bool,
};

HotelOrdersListCard.defaultProps = {
  order: {},
};

export default HotelOrdersListCard;
